import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "@vapor/react-material/Popover";
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from "@vapor/react-material";
import {
  faCircleCheck,
  faCircleExclamation,
  faFolderArrowDown,
  faRotateLeft,
  faSpinner,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "@onefront/react-sdk";
import { Document } from "../../document-page/state/useDocumentList";
import { ConfirmationDialog } from "../../common-components/ConfirmationDialog";
import { useCallback, useState } from "react";
import { useDocumentList } from "../../document-page/state/useDocumentList";

export enum ResponseStatus {
  OK = 200,
  INTERNAL_SERVER_ERROR = 500,
}

interface DataChangeDocumentStatusType {
  isUrgent?: boolean;
  key:
    | "restore"
    | "deleteDefinitely"
    | "addToDossier"
    | "isUrgent"
    | "read"
    | "delete";
  read?: boolean;
}

interface CardPopoverProps {
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  changeDocumentStatus: (
    documentId: string,
    workspaceId: string,
    documentActionChange: DataChangeDocumentStatusType
  ) => Promise<ResponseStatus>;
  doc: Document;
  handleCloseActionsPopup: () => void;
  handleDocumentsDelete: (documentId: string[]) => void;
  handleDocumentRestore: (documentId: string[]) => void;
  handleMoveToBin: (documentId: string[]) => void;
  isDeleteDocumentsLoading: boolean;
  isMoveToBinLoading: boolean;
  loadingChangeDocumentStatus: boolean;
  open: boolean;
  restoreLoading: boolean;
  workspaceId: string;
}

const CardPopover = ({
  anchorEl,
  changeDocumentStatus,
  doc,
  handleCloseActionsPopup,
  handleDocumentsDelete,
  handleDocumentRestore,
  handleMoveToBin,
  isDeleteDocumentsLoading,
  isMoveToBinLoading,
  loadingChangeDocumentStatus,
  open,
  restoreLoading,
  workspaceId,
}: CardPopoverProps) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openMoveToBinDialog, setOpenMoveToBinDialog] = useState(false);
  const [selectedButtonKey, setSelectedButtonKey] = useState<string | null>(
    null
  );
  const { t } = useTranslation("DOCS");
  const id = open ? "actions-popover" : undefined;

  const popoverOptions = {
    // This is documenty type properties for 3 dots menu
    CESTINO: [
      {
        icon: faRotateLeft,
        label: t("classification.restore"),
        key: "restore",
      },
      {
        icon: faTrashCan,
        label: t("confirmation.deletePermanently"),
        key: "deleteDefinitely",
      },
    ],

    OTHER: [
      {
        icon: faFolderArrowDown,
        label: t("document.addToDossier"),
        key: "addToDossier",
      },
      {
        icon: faCircleExclamation,
        label: t("document.setAsUrgent"),
        key: "isUrgent",
      },
      {
        icon: faCircleCheck,
        label: t("document.setAsRead"),
        key: "read",
      },
      {
        icon: faTrashCan,
        label: t("classification.delete"),
        key: "delete",
      },
    ],
  };

  const renderButtonDisplay = (label, doc) => {
    if (label.key === "isUrgent") {
      return doc.urgent
        ? t("document.setAsNonUrgent")
        : t("document.setAsUrgent");
    }

    if (label.key === "read") {
      return doc.status === "READ"
        ? t("document.setAsNonRead")
        : t("document.setAsRead");
    }

    return label.label || null;
  };

  const {
    documentFilterOption,
    handleDocumentUpdate,
    setDocumentStatus,
    setDocumentUrgent,
  } = useDocumentList();

  const handleButtonClick = useCallback(
    async (button, doc) => {
      setSelectedButtonKey(button.key);

      if (button.key === "read") {
        const currentRead = doc.status;
        const newRead = currentRead === "READ" ? false : true;
        const success = await changeDocumentStatus(
          doc.documentId,
          workspaceId,
          {
            key: button.key,
            read: newRead,
          }
        );
        if (success === 200) {
          setDocumentStatus(doc.documentId, newRead ? "READ" : "UNREAD");
          handleCloseActionsPopup();
        }
      } else if (button.key === "isUrgent") {
        const currentUrgent = doc.urgent;
        const success = await changeDocumentStatus(
          doc.documentId,
          workspaceId,
          { key: button.key, isUrgent: !currentUrgent }
        );
        if (success === 200) {
          if (documentFilterOption === "URGENTI") {
            handleDocumentUpdate([doc.documentId]);
          }
          setDocumentUrgent(doc.documentId, !currentUrgent);
          handleCloseActionsPopup();
        }
      } else if (button.key === "restore") {
        handleDocumentRestore([doc.documentId]);
      } else if (button.key === "deleteDefinitely") {
        setOpenDeleteDialog(true);
      } else if (button.key === "delete") {
        setOpenMoveToBinDialog(true);
      } else {
        return;
      }
    },
    [handleDocumentRestore]
  );

  const popoverProperty =
    documentFilterOption === "CESTINO" ? "CESTINO" : "OTHER";
  const popoverButtons = popoverOptions[popoverProperty] || [];

  const onDeleteDialogConfirm = () => {
    handleDocumentsDelete([doc.documentId]);
    setOpenDeleteDialog(false);
  };

  const onMoveToBinDialogConfirm = () => {
    handleMoveToBin([doc.documentId]);
    setOpenMoveToBinDialog(false);
  };

  return (
    <>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        id={id}
        onClose={handleCloseActionsPopup}
        open={open}
      >
        <MenuList sx={{ width: "240px", maxWidth: "100%" }}>
          {popoverButtons.map((option) => {
            let isLoading = false;
            if (option.key === "restore") {
              isLoading = restoreLoading;
            } else if (option.key === "deleteDefinitely") {
              isLoading = isDeleteDocumentsLoading;
            } else if (option.key === "delete") {
              isLoading = isMoveToBinLoading;
            } else {
              isLoading =
                loadingChangeDocumentStatus && selectedButtonKey === option.key;
            }
            return (
              <MenuItem
                disabled={
                  loadingChangeDocumentStatus &&
                  selectedButtonKey === option.key
                }
                key={option.key}
                onClick={() => handleButtonClick(option, doc)}
              >
                <ListItemIcon>
                  <FontAwesomeIcon
                    icon={isLoading ? faSpinner : option.icon}
                    spin={isLoading}
                    style={{ fontSize: "16px" }}
                  />
                </ListItemIcon>
                <ListItemText>{renderButtonDisplay(option, doc)}</ListItemText>
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
      <ConfirmationDialog
        cancelButtonColor="secondary"
        cancelButtonText={t("classification.cancel")}
        cancelButtonVariant="outlined"
        confirmButtonColor="error"
        confirmButtonText={t("confirmation.deletePermanently")}
        confirmButtonVariant="contained"
        content={t("confirmation.singlePermanentDeleteMessage", {
          fileName: doc.fileName,
        })}
        dividerVariant="alert"
        handleDialogClose={() => setOpenDeleteDialog(false)}
        handleDialogConfirm={onDeleteDialogConfirm}
        open={openDeleteDialog}
        title={t("confirmation.deletePermanently") + "?"}
      />
      <ConfirmationDialog
        cancelButtonColor="secondary"
        cancelButtonText={t("classification.cancel")}
        cancelButtonVariant="outlined"
        confirmButtonColor="primary"
        confirmButtonText={t("classification.delete")}
        confirmButtonVariant="contained"
        content={t("confirmation.singleMoveToBinMessage")}
        dividerVariant="fullWidth"
        handleDialogClose={() => setOpenMoveToBinDialog(false)}
        handleDialogConfirm={onMoveToBinDialogConfirm}
        open={openMoveToBinDialog}
        title={t("classification.delete")}
      />
    </>
  );
};

export default CardPopover;
